// repoStatusModel.ts — what a repository's status means, and what to do about it.
//
// The list API says how many REQUIRED checks fail (`failing_checks`), what the
// advisory (non-required) checks did (`advisory_checks`, absent on an older
// server) and
// whether the GitHub mirror works (`mirror`). The names and reasons come from
// `GET /api/v3/repos/{owner}/{repo}/commits/{branch}/check-runs`, fetched when
// somebody opens a repository's status. Every field is optional: the server may
// send fewer, and nothing here may throw.

import type {
  RepositoryAdvisoryChecks,
  RepositoryMirrorStatus
} from '../api/types';

/** The check that records merge-to-GitHub mirror pushes. */
export const MIRROR_CHECK = 'jeryu/github-mirror';

/** The forge's required audit proof context. */
export const JANKURAI_PROOF_CHECK = 'jankurai/proof';

/** Said wherever a failing mirror is explained: the table, and repo Settings. */
export const MIRROR_OPERATOR_SENTENCE =
  'The forge host cannot push to GitHub (missing SSH rewrite or deploy key on the server). An operator fixes it on the host; merges are not affected.';

export interface CheckRunOutput {
  title?: string | null;
  summary?: string | null;
}

export interface CheckRun {
  name?: string;
  conclusion?: string | null;
  output?: CheckRunOutput | null;
  details_url?: string | null;
  completed_at?: string | null;
  started_at?: string | null;
}

export interface CheckRunsResponse {
  check_runs?: CheckRun[];
}

export interface FailingCheck {
  name: string;
  title: string | null;
  /** The first lines of the check's own summary. */
  summary: string[];
  detailsUrl: string | null;
  whatToDo: string;
}

/** One plain sentence: the next step for a failing check, by its name. */
export function whatToDo(checkName: string): string {
  if (checkName === JANKURAI_PROOF_CHECK) {
    return 'Raise the audit score to the floor: fix the listed caps or findings on the default branch, then push; the proof re-runs on the next push.';
  }
  if (checkName === MIRROR_CHECK) return MIRROR_OPERATOR_SENTENCE;
  if (checkName === 'pr-gate-runner' || checkName.endsWith('/required')) {
    return "The required gate is red on the default branch: open the newest pull request's pipeline panel or Activity for the log.";
  }
  return 'Open the check for its log.';
}

/** The first `limit` non-empty lines of a check summary, list markers removed. */
export function summaryLines(
  summary: string | null | undefined,
  limit = 4
): string[] {
  if (!summary) return [];
  return summary
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-*]\s+/, '').trim())
    .filter((line) => line.length > 0)
    .slice(0, limit)
    .map((line) => (line.length > 220 ? `${line.slice(0, 219)}…` : line));
}

function when(run: CheckRun): string {
  return run.completed_at ?? run.started_at ?? '';
}

/**
 * The failing checks of one commit, each name once (its newest run), in name
 * order. A check whose newest run passed is not failing, whatever came before.
 */
export function failingChecks(
  response: CheckRunsResponse | null | undefined
): FailingCheck[] {
  const newest = new Map<string, CheckRun>();
  for (const run of response?.check_runs ?? []) {
    if (!run.name) continue;
    const held = newest.get(run.name);
    if (!held || when(run) >= when(held)) newest.set(run.name, run);
  }
  return [...newest.entries()]
    .filter(([, run]) => run.conclusion === 'failure')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, run]) => ({
      name,
      title: run.output?.title?.trim() || null,
      summary: summaryLines(run.output?.summary),
      detailsUrl: run.details_url?.trim() || null,
      whatToDo: whatToDo(name)
    }));
}

/** A mirror that is configured and whose last push failed. */
export function mirrorFailing(
  mirror: RepositoryMirrorStatus | null | undefined
): boolean {
  return Boolean(mirror?.configured) && mirror?.last_attempt_ok === false;
}

/** "1 failing check" / "3 failing checks". */
export function failingLabel(count: number): string {
  return `${count} failing check${count === 1 ? '' : 's'}`;
}

/** What advisory (non-required) results are not: the reason they sit apart. */
export const ADVISORY_SENTENCE =
  'Advisory checks are not required to merge and do not count against health.';

/**
 * "1 advisory check failing" / "2 advisory checks failing", or `null` when the
 * server reports no advisory results or none of them fail. A passing advisory
 * check is said in `advisoryTitle`, not on the row: the chip is for what a
 * person may want to look at.
 */
export function advisoryLabel(
  advisory: RepositoryAdvisoryChecks | null | undefined
): string | null {
  const failing = advisory?.failing ?? 0;
  if (failing < 1) return null;
  return `${failing} advisory check${failing === 1 ? '' : 's'} failing`;
}

/** The whole advisory tally, for the chip's hover text. */
export function advisoryTitle(advisory: RepositoryAdvisoryChecks): string {
  return `${advisory.failing} failing, ${advisory.passing} passing. ${ADVISORY_SENTENCE}`;
}

/** Sort key for the Status column: failing checks first, then a failing mirror. */
export function attentionRank(repo: {
  failing_checks: number;
  mirror?: RepositoryMirrorStatus | null;
}): number {
  return repo.failing_checks * 2 + (mirrorFailing(repo.mirror) ? 1 : 0);
}

export interface MirrorFacts {
  configured: boolean;
  headline: string;
  lastAttempt: string | null;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  failing: boolean;
}

/** The GitHub mirror of one repository, as facts for the Settings page. */
export function mirrorFacts(
  mirror: RepositoryMirrorStatus | null | undefined
): MirrorFacts {
  if (!mirror?.configured) {
    return {
      configured: false,
      headline: 'This repository is not mirrored to GitHub.',
      lastAttempt: null,
      lastAttemptAt: null,
      lastSuccessAt: null,
      failing: false
    };
  }
  const failing = mirror.last_attempt_ok === false;
  const outcome = mirror.last_attempt_at
    ? failing
      ? (mirror.last_attempt_conclusion ?? 'failure')
      : 'success'
    : null;
  return {
    configured: true,
    headline: failing
      ? 'Merges to the default branch are pushed to GitHub, and the last push failed.'
      : 'Merges to the default branch are pushed to GitHub.',
    lastAttempt: outcome,
    lastAttemptAt: mirror.last_attempt_at ?? null,
    lastSuccessAt: mirror.last_success_at ?? null,
    failing
  };
}

/** A repository another owner holds a more recently updated copy of. */
export interface NewerCopy {
  owner: string;
  name: string;
}

const COPY_AGE_GAP_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Repositories whose NAME also exists under another owner that was updated at
 * least a week later: `owner/name` of the older copy -> the newer one. The
 * forge has stale snapshots beside live repositories (jeryu/jain next to
 * veox/jain) and nothing told a reader, or an agent, which one is current.
 * Copies updated within a week of each other are left alone: no clear winner.
 */
export function newerCopies(
  repos: { id: { owner: string; name: string }; updated_at?: string | null }[]
): Map<string, NewerCopy> {
  const newestByName = new Map<string, { owner: string; at: number }>();
  const stamp = (value: string | null | undefined): number => {
    const at = Date.parse(value ?? '');
    return Number.isNaN(at) ? 0 : at;
  };
  for (const repo of repos) {
    const at = stamp(repo.updated_at);
    const held = newestByName.get(repo.id.name);
    if (!held || at > held.at) newestByName.set(repo.id.name, { owner: repo.id.owner, at });
  }
  const older = new Map<string, NewerCopy>();
  for (const repo of repos) {
    const newest = newestByName.get(repo.id.name);
    if (!newest || newest.owner === repo.id.owner) continue;
    if (newest.at - stamp(repo.updated_at) < COPY_AGE_GAP_MS) continue;
    older.set(`${repo.id.owner}/${repo.id.name}`, { owner: newest.owner, name: repo.id.name });
  }
  return older;
}
